import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button, ErrorBox } from './ui';
import { buildShareUrl } from '../lib/storage';

/**
 * 共有ダイアログ（§9.2 / §4.1 G-02）。
 *
 * トークンをURLに埋める方式である以上、権限の説明は隠さず併記する。
 * QRコード表示はフェーズ6で追加する。
 */
export function ShareSheet({
  groupId,
  token,
  groupName,
  onClose,
}: {
  groupId: string;
  token: string;
  groupName: string;
  onClose: () => void;
}) {
  const url = buildShareUrl(groupId, token);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  // 対面ならQR、離れているならURLコピーを選べるようにする（画面案10）。
  // data URI にするので外部へのリクエストは発生しない（CSP の img-src data: 内）。
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(url, {
      width: 252,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#232B26', light: '#FFFFFF' },
    })
      .then((dataUrl) => {
        if (alive) setQr(dataUrl);
      })
      .catch(() => {
        // QR が出せなくても URL コピーで共有できる
        if (alive) setQr(null);
      });
    return () => {
      alive = false;
    };
  }, [url]);

  async function copy() {
    setCopyFailed(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // HTTPS でない、または権限が拒否された場合。URL は画面に出ているので
      // 手で選択してもらう
      setCopyFailed(true);
    }
  }

  async function share() {
    if (!navigator.share) {
      void copy();
      return;
    }
    try {
      await navigator.share({ title: groupName, text: `${groupName}のスコア表`, url });
    } catch {
      // ユーザーが共有シートを閉じただけ。何もしない
    }
  }

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center">
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        className="absolute inset-0 bg-felt/45"
      />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl border-t border-line bg-white px-4 pb-6 pt-5">
        <div>
          <div className="text-base font-bold">{groupName}に招待する</div>
          <div className="mt-0.5 text-[11.5px] text-ink-faint">
            読み込んだ人はすぐに記録を追加できます
          </div>
        </div>

        {qr && (
          <img
            src={qr}
            alt="参加用QRコード"
            width={126}
            height={126}
            className="mx-auto mt-4 rounded-lg border border-line bg-white p-2"
          />
        )}

        <div className="mt-3.5 break-all rounded-lg bg-bone px-3 py-2.5 text-[11.5px] leading-relaxed text-ink-soft">
          {url}
        </div>

        <div className="mt-3.5 flex flex-col gap-2">
          <Button onClick={copy}>{copied ? 'コピーしました' : 'URLをコピー'}</Button>
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <Button variant="ghost" onClick={share}>
              ほかのアプリで共有
            </Button>
          )}
        </div>

        {copyFailed && (
          <div className="mt-3">
            <ErrorBox message="コピーできませんでした。上のURLを長押しして選択してください。" />
          </div>
        )}

        <div className="mt-3.5 rounded-lg border-l-[3px] border-red bg-[#FBEDEB] px-3 py-2.5 text-[12.5px] leading-relaxed text-[#8C2A24]">
          このURLを知っている人は誰でも、記録の追加・編集・削除ができます。共有する相手にご注意ください。
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full py-2 text-[13px] text-ink-faint"
        >
          閉じる
        </button>
      </div>
    </div>
  );
}
